import {
    acceptanceBucketTypeIds,
    acceptanceBucketTypeSortKey,
    acceptanceBucketTypes,
    registrationGroupStatuses,
    registrationGroupStatusTypeIds,
    registrationGroupStatusTypes,
    registrationStatusTypes,
    waitlistBucketTypeIds,
    waitlistBucketTypes,
    waitlistBucketTypeSortKey,
} from '../../../../../utils/eventConstants';
import { mergeArraysOfObjectsById } from '../../../../../utils/misc';
import { comparator } from '../../../../../utils/comparator';
import { getComparatorArguments, setPlayerListPrimarySortValue, setTeamListPrimarySortValue } from './utils';

export const createPlayerListAccumulator = () => ({
    teamList: [],
    withdrawnTeamList: [],
    memberIds: [],
    playerCount: 0,
    playerStatuses: {
        REGISTERED: false,
        NEED_PARTNER: false,
        PENDING: false,
        WITHDRAWN: false,
    },
    playerPositions: {
        MAIN: false,
        WAITLIST: false,
        NEED_PARTNER: false,
        QUALIFYING: false,
        ALTERNATE: false,
        WITHDRAWN: false,
    },
});

export const recordPlayerPosition = (playerPositions, isAcceptance, bucketTypeId) => {
    const sourceMap = isAcceptance ? acceptanceBucketTypeIds : waitlistBucketTypeIds;
    const key = sourceMap[bucketTypeId]?.toUpperCase().replace(' ', '_');
    if (key) {
        playerPositions[key] = true;
    }
};

export const buildTeamObject = (playersDictByMemberId, primaryMemberId, partnerMemberId) => {
    const team = { players: [] };
    if (primaryMemberId && partnerMemberId) {
        team.players.push(playersDictByMemberId[primaryMemberId]);
        team.players.push(playersDictByMemberId[partnerMemberId]);
    } else if (primaryMemberId) {
        team.players.push(playersDictByMemberId[primaryMemberId]);
    }

    return team;
};

const isPositionVisible = (showPlayers, showNeedPartners, player, playerDivision, authUserId) => {
    if (showPlayers) {
        return true;
    }

    if (player.memberId === authUserId) {
        return true;
    }

    if (showNeedPartners && playerDivision.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER) {
        return true;
    }

    return false;
};

const applyPartnerDivisionStatus = (partnerDivision, acc) => {
    if (partnerDivision.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER) {
        // Preserved pre-existing behavior: writes onto partnerDivision rather than acc.playerStatuses.
        partnerDivision.NEED_PARTNER = true;
        return;
    }
    acc.playerStatuses[registrationStatusTypes[partnerDivision.registrationStatusTypeId]] = true;
};

const applyPartner = (player, playerDivision, ctx, acc) => {
    const { playersTabState, playersData, showPlayers, showNeedPartners, authUserId } = ctx;
    const partnerIds = playerDivision.partners || [];
    let partnerMemberId;
    let partnerTriggersShow = false;

    partnerIds.every(memberId => {
        const partner = playersData?.find(p => p.memberId === memberId);
        if (!partner) {
            return null;
        }
        const partnerDivision = partner.divisions?.find(div => div.id === playersTabState.divisionId);
        applyPartnerDivisionStatus(partnerDivision, acc);
        if (isPositionVisible(showPlayers, showNeedPartners, player, playerDivision, authUserId)) {
            recordPlayerPosition(acc.playerPositions, playerDivision.isAcceptance, playerDivision.type);
        }
        if (shouldPlayerBeDisplayed(partnerDivision, false, playersTabState)) {
            partnerTriggersShow = true;
        }
        acc.memberIds.push(memberId);
        partnerMemberId = memberId;
        return false;
    });

    return { partnerMemberId, partnerTriggersShow };
};

const pushPlayerTeam = (player, playerDivision, partnerMemberId, showTeam, ctx, acc) => {
    const { playersTabState, playersDictByMemberId, invites } = ctx;
    const team = buildTeamObject(playersDictByMemberId, player.memberId, partnerMemberId);
    const invite = invites.find(inv => inv.memberId === player.memberId && inv.divisionId === playersTabState.divisionId);
    if (invite) {
        team.invite = invite;
    }

    if (!team.players.length) {
        return;
    }

    acc.playerCount += team.players.length;
    if (team.invite) {
        acc.playerCount += 1;
    }

    if (!showTeam) {
        return;
    }

    const searchableName = `${team.players[0]?.firstName} ${team.players[0]?.lastName} ${team.players[1]?.firstName} ${team.players[1]?.lastName}`;
    acc.teamList.push({
        ...team,
        type: playerDivision.type,
        order: playerDivision.order,
        position: playerDivision.position,
        searchableName,
    });
};

export const processRegisteredPlayer = (player, ctx, acc) => {
    const { playersTabState, authUserId, showPlayers, showNeedPartners } = ctx;

    const playerDivision = player.divisions?.find(div => div.id === playersTabState.divisionId);
    if (!playerDivision) {
        return;
    }

    if (playerDivision.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER) {
        acc.playerStatuses.NEED_PARTNER = true;
    } else {
        acc.playerStatuses[registrationStatusTypes[playerDivision.registrationStatusTypeId]] = true;
    }

    if (isPositionVisible(showPlayers, showNeedPartners, player, playerDivision, authUserId)) {
        recordPlayerPosition(acc.playerPositions, playerDivision.isAcceptance, playerDivision.type);
    }

    const filterSelected = playersTabState.selectedStatus.value || playersTabState.selectedPosition.value;
    let showTeam = !filterSelected || shouldPlayerBeDisplayed(playerDivision, false, playersTabState);

    if (acc.memberIds.includes(player.memberId)) {
        return;
    }
    acc.memberIds.push(player.memberId);

    const { partnerMemberId, partnerTriggersShow } = applyPartner(player, playerDivision, ctx, acc);
    if (partnerTriggersShow) {
        showTeam = true;
    }

    pushPlayerTeam(player, playerDivision, partnerMemberId, showTeam, ctx, acc);
};

export const processWithdrawnPlayer = (player, ctx, acc) => {
    const { playersTabState, playersDictByMemberId } = ctx;
    const playerDivision = player.divisions?.find(div => div.id === playersTabState.divisionId);
    if (!playerDivision || acc.memberIds.includes(player.memberId)) {
        return;
    }

    acc.memberIds.push(player.memberId);
    const team = buildTeamObject(playersDictByMemberId, player.memberId);
    if (team && team.players.length > 0) {
        acc.playerCount += team.players.length;
        if (!playersTabState.selectedStatus.value || playersTabState.selectedStatus.value === registrationGroupStatuses.WITHDRAWN) {
            acc.withdrawnTeamList.push({
                ...team,
                bucketType: waitlistBucketTypes.WITHDRAWN,
                order: waitlistBucketTypeSortKey.WITHDRAWN,
            });
        }
        acc.playerStatuses.WITHDRAWN = true;
        acc.playerPositions.WITHDRAWN = true;
    }
};

export function shouldPlayerBeDisplayed(playerDivision, isWithdrawn, playersTabState) {
    const selectedStatusValue = playersTabState.selectedStatus?.value;
    const selectedPositionValue = playersTabState.selectedPosition?.value;

    const isStatusMatch =
        !selectedStatusValue ||
        (selectedStatusValue === registrationGroupStatuses.NEED_PARTNER &&
            selectedStatusValue === registrationGroupStatusTypes[playerDivision.registrationGroupStatus] &&
            !isWithdrawn) ||
        selectedStatusValue === registrationStatusTypes[playerDivision.registrationStatusTypeId];

    if (!isStatusMatch) {
        return false;
    }

    if (!selectedPositionValue) {
        return true;
    }

    const positionMap = playerDivision.isAcceptance ? acceptanceBucketTypeIds : waitlistBucketTypeIds;
    const positionKey = positionMap[playerDivision.type]?.toUpperCase().replace(' ', '_');
    return selectedPositionValue === positionKey;
}

export const processAllDivisionsView = (players, ctx, acc) => {
    const { authUserId, showPlayers, showNeedPartners, isCurrentMemberAdmin } = ctx;
    if (!players.data) {
        return [];
    }

    players.data.forEach(player => {
        player.divisions.forEach(div => {
            if (div.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER) {
                acc.playerStatuses.NEED_PARTNER = true;
            } else {
                acc.playerStatuses[registrationStatusTypes[div.registrationStatusTypeId]] = true;
            }
            if (isPositionVisible(showPlayers, showNeedPartners, player, div, authUserId)) {
                recordPlayerPosition(acc.playerPositions, div.isAcceptance, div.type);
            }
        });
    });

    if (!players.withdrawn?.length || !isCurrentMemberAdmin) {
        return [...players.data];
    }

    acc.playerStatuses.WITHDRAWN = true;
    acc.playerPositions.WITHDRAWN = true;

    const mergedByPlayerId = new Map();
    const withdrawnOnly = [];

    players.withdrawn.forEach(wp => {
        const dataPlayer = players.data.find(p => p.playerId === wp.playerId);
        if (dataPlayer) {
            mergedByPlayerId.set(wp.playerId, {
                ...dataPlayer,
                divisions: mergeArraysOfObjectsById(dataPlayer.divisions, wp.divisions),
            });
        } else {
            withdrawnOnly.push({ ...wp, isWithdrawn: true });
        }
    });

    const data = players.data.map(p => mergedByPlayerId.get(p.playerId) || p);
    return [...data, ...withdrawnOnly];
};

export const processAcceptanceView = (acceptanceData, acc) => {
    const buckets = acceptanceData?.buckets || [];

    buckets.forEach(bucket => {
        recordPlayerPosition(acc.playerPositions, true, bucket.type);
    });

    if (buckets.some(bucket => bucket.players.length > 0)) {
        acc.playerStatuses.REGISTERED = true;
    }

    if ((acceptanceData?.withdrawnPlayers || []).length > 0) {
        acc.playerStatuses.WITHDRAWN = true;
    }

    if ((acceptanceData?.withdrawnPlayersFromBuckets || []).length > 0) {
        acc.playerPositions.WITHDRAWN = true;
    }

    const bucketPositionsToCheck = {
        [acceptanceBucketTypes.MAIN]: acceptanceBucketTypeSortKey.MAIN,
        [acceptanceBucketTypes.QUALIFYING]: acceptanceBucketTypeSortKey.QUALIFYING,
        [acceptanceBucketTypes.ALTERNATE]: acceptanceBucketTypeSortKey.ALTERNATE,
    };

    Object.keys(bucketPositionsToCheck).forEach(positionKey => {
        const bucketTypeId = bucketPositionsToCheck[positionKey];
        const bucket = buckets.find(b => parseInt(b.type, 10) === parseInt(bucketTypeId, 10));
        acc.playerPositions[positionKey] = !!bucket && bucket.players.length > 0;
    });
};

export const buildPlayerListsAndCounts = ({
    event,
    players,
    playersTabState,
    auth,
    invites,
    acceptance,
    isCurrentMemberAdmin,
    showPlayers,
    showNeedPartners,
    selectedDivision,
    utrTypeId,
}) => {
    const reducerCtx = {
        playersTabState,
        authUserId: auth.user?.id,
        invites,
        playersData: players.data,
        playersDictByMemberId: players.playersDictByMemberId,
        showPlayers,
        showNeedPartners,
        isCurrentMemberAdmin,
    };

    const acc = createPlayerListAccumulator();
    let mergedPlayerList = [];
    if (event.data && !selectedDivision) {
        mergedPlayerList = processAllDivisionsView(players, reducerCtx, acc);
    }

    let filtered = mergedPlayerList;
    if (playersTabState.selectedStatus?.value || playersTabState.selectedPosition?.value) {
        filtered = players.data ? mergedPlayerList.filter(player => {
            return player.divisions.some(div => shouldPlayerBeDisplayed(div, player.isWithdrawn, playersTabState));
        }) : [];
    }

    if (event.data && !selectedDivision?.isAcceptance) {
        if (players.data?.length > 0) {
            players.data.forEach(player => processRegisteredPlayer(player, reducerCtx, acc));
        }
        if (players.withdrawn?.length > 0 && isCurrentMemberAdmin) {
            players.withdrawn.forEach(player => processWithdrawnPlayer(player, reducerCtx, acc));
        }
    }

    if (event.data && selectedDivision?.isAcceptance) {
        processAcceptanceView(acceptance?.[playersTabState.divisionId], acc);
    }

    const userIsPowered = !!auth.user?.isPowered;
    const { sortParam, sortType } = playersTabState || {};
    const sortedTeamList = setTeamListPrimarySortValue(acc.teamList, sortParam, utrTypeId, userIsPowered, isCurrentMemberAdmin);
    const sortedFiltered = setPlayerListPrimarySortValue(filtered, sortParam, utrTypeId, userIsPowered, isCurrentMemberAdmin);
    const sortArgs = getComparatorArguments(sortParam, sortType);
    sortedTeamList.sort(comparator(sortArgs));
    sortedFiltered.sort(comparator(sortArgs));

    return {
        teamList: sortedTeamList,
        filteredPlayers: sortedFiltered,
        withdrawnTeamList: acc.withdrawnTeamList,
        playerStatuses: acc.playerStatuses,
        playerPositions: acc.playerPositions,
        playerCount: acc.playerCount,
    };
};
